import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Coins, Trophy, Users, Zap, Copy, Rocket, Store, Flame, Crown, Medal, Heart, Sparkles } from 'lucide-react';

export function CashoutPage() {
  const { user, refresh } = useAuth();
  const [summary, setSummary] = useState(null);
  const [method, setMethod] = useState('paypal');
  const [details, setDetails] = useState('');
  const [history, setHistory] = useState([]);

  const load = async () => {
    const s = await api.get('/jxp/summary'); setSummary(s.data);
    const h = await api.get('/cashout/mine'); setHistory(h.data);
  };
  useEffect(() => { if (user) load(); }, [user]);

  const submit = async () => {
    try {
      await api.post('/cashout/request', { method, details });
      toast.success('Cash-out requested — under review');
      setDetails(''); await load(); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="font-display text-3xl font-bold">Cash out</h1>
      <Card className="glass p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-violet-400 font-semibold">Progress</div>
            <div className="font-display text-3xl font-black mt-1">
              ${summary?.cashout_usd_progress || 0} <span className="text-slate-500 text-xl font-normal">of ${summary?.cashout_min_usd || 5}</span>
            </div>
          </div>
          <Coins className="w-10 h-10 text-amber-400" />
        </div>
        <Progress data-testid="cashout-progress-bar" value={summary?.cashout_progress_pct || 0} className="h-3 mt-4" />
        <div className="text-xs text-slate-500 mt-2">Cash-out fee: ${summary?.cashout_fee_usd}. Exact per-activity rewards are calculated internally.</div>
      </Card>
      <Card className="glass p-6 space-y-4">
        <div className="font-display font-bold text-lg">Request payout</div>
        <div><Label>Method</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="cash-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="paypal">PayPal</SelectItem>
              <SelectItem value="giftcard">Gift Card</SelectItem>
              <SelectItem value="bank">Bank Transfer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Details</Label>
          <Input data-testid="cash-details" value={details} onChange={e => setDetails(e.target.value)} placeholder="PayPal email / preferred gift card / bank last-4" />
        </div>
        <Button data-testid="cash-submit" disabled={!summary?.eligible} onClick={submit} className="gradient-brand text-white w-full">
          {summary?.eligible ? 'Request payout' : 'Not yet eligible'}
        </Button>
      </Card>
      {history.length > 0 && (
        <Card className="glass p-6">
          <div className="font-display font-bold text-lg mb-3">History</div>
          <div className="space-y-2">
            {history.map(h => (
              <div key={h.id} className="flex justify-between text-sm p-3 rounded-lg bg-white/5">
                <div><div className="font-semibold capitalize">{h.method}</div><div className="text-xs text-slate-400">{new Date(h.created_at).toLocaleDateString()}</div></div>
                <div className="text-right"><div className="font-bold">${h.usd_amount}</div><div className="text-xs uppercase tracking-wider">{h.status}</div></div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

export function SpinPage() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (user) api.get('/spin/status').then(r => setStatus(r.data)); }, [user]);
  const spin = async () => {
    setSpinning(true); setResult(null);
    try {
      const { data } = await api.post('/spin');
      setTimeout(() => { setResult(data.reward); setSpinning(false); refresh(); setStatus({ can_spin: false }); }, 2500);
    } catch (e) { setSpinning(false); toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="max-w-lg mx-auto text-center space-y-6">
      <h1 className="font-display text-3xl font-bold">Daily Spin Wheel</h1>
      <div className="relative mx-auto w-64 h-64 sm:w-80 sm:h-80">
        <div className={`w-full h-full rounded-full gradient-brand shadow-2xl shadow-violet-500/40 ${spinning ? 'animate-spin' : ''} transition-transform`} style={{ animationDuration: '0.5s' }}>
          <div className="w-full h-full rounded-full flex items-center justify-center">
            <Zap className="w-24 h-24 text-white" />
          </div>
        </div>
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-6 h-6 rotate-45 bg-amber-400" />
      </div>
      {result && <Card data-testid="spin-result" className="glass p-4 font-display font-bold text-xl">🎉 You won: {result.label}</Card>}
      <Button data-testid="spin-btn" disabled={!status?.can_spin || spinning} onClick={spin} className="gradient-brand text-white text-lg px-8 py-6">
        {status?.can_spin ? (spinning ? 'Spinning…' : 'Spin!') : 'Come back tomorrow'}
      </Button>
    </div>
  );
}

export function MissionsPage() {
  const [missions, setMissions] = useState([]);
  useEffect(() => { api.get('/missions/today').then(r => setMissions(r.data)); }, []);
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <h1 className="font-display text-3xl font-bold">Daily Missions</h1>
      {missions.map(m => (
        <Card key={m.key} data-testid={`mission-full-${m.key}`} className="glass p-4">
          <div className="flex justify-between mb-2">
            <span className="font-semibold">{m.title}</span>
            <span className={`text-xs font-bold px-2 py-1 rounded ${m.completed ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
              {m.completed ? '✓ Done' : `+${m.reward} JXP`}
            </span>
          </div>
          <Progress value={Math.min(100, (m.progress / m.target) * 100)} className="h-2" />
          <div className="text-xs text-slate-400 mt-1">{m.progress}/{m.target}</div>
        </Card>
      ))}
    </div>
  );
}

export function ReferralsPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  useEffect(() => { if (user) api.get('/referrals/stats').then(r => setStats(r.data)); }, [user]);
  const link = user ? `${window.location.origin}/register?ref=${user.referral_code}` : '';
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Users className="w-7 h-7 text-emerald-400" /> Refer & Earn</h1>
      <Card className="glass p-6">
        <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">Your code</div>
        <div className="font-display text-3xl font-black mt-1" data-testid="ref-code">{user?.referral_code}</div>
        <div className="flex gap-2 mt-4">
          <Input value={link} readOnly />
          <Button data-testid="ref-copy" onClick={() => { navigator.clipboard.writeText(link); toast.success('Link copied'); }}><Copy className="w-4 h-4" /></Button>
        </div>
      </Card>
      <Card className="glass p-6">
        <div className="text-slate-400 text-sm">Total referrals</div>
        <div className="font-display text-4xl font-black">{stats?.total || 0}</div>
      </Card>
    </div>
  );
}

export function LeaderboardsPage() {
  const [kind, setKind] = useState('jxp');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    api.get(`/leaderboards/${kind}`)
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [kind]);

  // Normalize entries so we always have {user, value}
  const rows = items.map(it => {
    const u = it.user || it;
    let value = 0;
    if (kind === 'jxp')         value = u.jxp || 0;
    else if (kind === 'streak') value = u.streak || 0;
    else if (kind === 'referrals') value = it.count || 0;
    else if (kind === 'creators')  value = it.likes || 0;
    return { user: u, value };
  });

  // Find current user's rank (if any)
  const myRankIdx = user ? rows.findIndex(r => r.user?.id === user.id) : -1;
  const myRow = myRankIdx >= 0 ? { ...rows[myRankIdx], rank: myRankIdx + 1 } : null;

  const META = {
    jxp:       { label: 'Top JXP',    Icon: Coins,  unit: 'JXP',   grad: 'from-amber-400 to-orange-500', tint: 'text-amber-300' },
    streak:    { label: 'Streaks',    Icon: Flame,  unit: 'days',  grad: 'from-emerald-400 to-teal-500', tint: 'text-emerald-300' },
    referrals: { label: 'Referrals',  Icon: Users,  unit: 'invites', grad: 'from-cyan-400 to-blue-500',  tint: 'text-cyan-300' },
    creators:  { label: 'Creators',   Icon: Heart,  unit: 'likes', grad: 'from-pink-500 to-fuchsia-500', tint: 'text-pink-300' },
  };
  const m = META[kind];

  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3, 50);
  // podium order: [#2, #1, #3] so #1 is in the middle & taller
  const podiumOrder = [top3[1], top3[0], top3[2]];
  const podiumHeights = ['h-28 sm:h-32', 'h-36 sm:h-44', 'h-24 sm:h-28'];
  const podiumMedals = [
    <Medal className="w-5 h-5 text-slate-300" key="s" />,
    <Crown className="w-6 h-6 text-amber-300" key="g" />,
    <Medal className="w-5 h-5 text-orange-400" key="b" />,
  ];
  const podiumRanks = ['2', '1', '3'];
  const podiumGrads = [
    'from-slate-400 to-slate-500',
    'from-amber-400 to-yellow-500',
    'from-orange-500 to-red-500',
  ];

  const fmt = (n) => (n || 0).toLocaleString();

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="text-center sm:text-left">
        <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center justify-center sm:justify-start gap-2">
          <Trophy className="w-7 h-7 text-amber-400" /> Leaderboards
        </h1>
        <p className="text-slate-400 text-sm mt-1">The top Jixxeo AI creators, earners and streakers this season.</p>
      </div>

      {/* Category tabs — scrollable pills so they never overflow on mobile */}
      <div data-testid="lb-tabs" className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
        {Object.entries(META).map(([k, meta]) => {
          const active = kind === k;
          const Icon = meta.Icon;
          return (
            <button
              key={k}
              data-testid={`lb-${k}`}
              onClick={() => setKind(k)}
              className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold border transition-all
                ${active
                  ? `bg-gradient-to-r ${meta.grad} text-slate-950 border-transparent shadow-lg`
                  : 'bg-white/5 border-white/10 text-slate-300 hover:border-white/30'}`}
            >
              <Icon className="w-4 h-4" /> {meta.label}
            </button>
          );
        })}
      </div>

      {/* Podium */}
      {!loading && top3.length > 0 && (
        <div data-testid="lb-podium" className="relative glass rounded-2xl p-4 sm:p-6 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-3 gap-3 items-end">
            {podiumOrder.map((entry, idx) => {
              if (!entry) {
                return (
                  <div key={idx} className={`flex flex-col items-center ${podiumHeights[idx]} justify-end opacity-30`}>
                    <div className="w-14 h-14 rounded-full bg-white/5 mb-2" />
                    <div className={`w-full ${podiumHeights[idx]} rounded-t-xl bg-white/5`} />
                  </div>
                );
              }
              const letter = (entry.user.display_name || entry.user.username || '?')[0]?.toUpperCase();
              const isMe = user?.id === entry.user?.id;
              return (
                <div key={idx} data-testid={isMe ? 'lb-podium-me' : undefined} className="flex flex-col items-center justify-end">
                  <div className="relative mb-1">{podiumMedals[idx]}</div>
                  <div className={`relative w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br ${podiumGrads[idx]} flex items-center justify-center text-xl sm:text-2xl font-black text-slate-950 shadow-lg mb-2 overflow-hidden
                    ${isMe ? 'ring-4 ring-violet-400 ring-offset-2 ring-offset-slate-950' : ''}`}>
                    {entry.user.avatar_url
                      ? <img src={entry.user.avatar_url} alt="" className="w-full h-full object-cover" />
                      : letter}
                    {isMe && (
                      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-violet-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-black uppercase tracking-wider shadow">You</span>
                    )}
                  </div>
                  <div className="text-xs font-bold text-center truncate w-full px-1">{entry.user.display_name || entry.user.username}</div>
                  <div className={`text-[11px] text-slate-400 truncate w-full text-center px-1`}>@{entry.user.username}</div>
                  <div className={`relative w-full ${podiumHeights[idx]} mt-2 rounded-t-xl bg-gradient-to-t ${podiumGrads[idx]} opacity-90 flex items-center justify-center`}>
                    <div className="absolute inset-0 bg-black/20 rounded-t-xl" />
                    <div className="relative text-center">
                      <div className="font-display font-black text-xl sm:text-2xl text-slate-950">#{podiumRanks[idx]}</div>
                      <div className="text-[10px] sm:text-xs font-bold text-slate-950/80">{fmt(entry.value)} {m.unit}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* My rank callout (only if user is on the board and not top 3) */}
      {myRow && myRow.rank > 3 && (
        <Card data-testid="lb-my-rank" className={`glass p-3 border-violet-500/40 bg-gradient-to-r from-violet-500/10 to-cyan-500/10`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-brand flex items-center justify-center font-black text-white shrink-0">
              #{myRow.rank}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-400">Your rank in {m.label}</div>
              <div className="font-semibold truncate">{myRow.user.display_name || myRow.user.username}</div>
            </div>
            <div className="text-right">
              <div className={`font-display font-black ${m.tint}`}>{fmt(myRow.value)}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">{m.unit}</div>
            </div>
          </div>
        </Card>
      )}
      {/* Unranked callout — logged in but not on the board at all */}
      {!loading && user && !myRow && (
        <Card data-testid="lb-unranked" className="glass p-3 border-dashed border-white/15">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-slate-500 shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">You're not ranked yet in {m.label}</div>
              <div className="text-xs text-slate-500">Keep creating — your name will show up here soon.</div>
            </div>
          </div>
        </Card>
      )}

      {/* Rest of the ladder */}
      <Card className="glass p-0 overflow-hidden">
        {loading && <div className="p-10 text-center text-slate-400 text-sm">Loading rankings…</div>}
        {!loading && rest.length === 0 && top3.length === 0 && (
          <div className="p-10 text-center">
            <Sparkles className="w-10 h-10 text-violet-400 mx-auto mb-2" />
            <div className="font-display font-bold">No entries yet</div>
            <div className="text-xs text-slate-500 mt-1">Be the first on this board.</div>
          </div>
        )}
        {!loading && rest.map((row, i) => {
          const rank = i + 4;
          const isMe = user?.id === row.user?.id;
          const letter = (row.user.display_name || row.user.username || '?')[0]?.toUpperCase();
          return (
            <div key={row.user?.id || i}
                 data-testid={`lb-row-${rank}`}
                 className={`flex items-center gap-3 px-3 sm:px-4 py-2.5 border-b border-white/5 last:border-0 transition-colors
                   ${isMe ? 'bg-violet-500/10' : 'hover:bg-white/5'}`}>
              <div className={`w-8 text-center font-black ${isMe ? 'text-violet-300' : 'text-slate-500'}`}>#{rank}</div>
              <div className="w-9 h-9 rounded-full gradient-brand flex items-center justify-center text-sm font-bold text-white overflow-hidden shrink-0">
                {row.user.avatar_url
                  ? <img src={row.user.avatar_url} alt="" className="w-full h-full object-cover" />
                  : letter}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">
                  {row.user.display_name || row.user.username}
                  {isMe && <span className="ml-2 text-[10px] uppercase tracking-wider text-violet-300">You</span>}
                </div>
                <div className="text-[11px] text-slate-500 truncate">@{row.user.username}</div>
              </div>
              <div className="text-right">
                <div className={`font-display font-black ${m.tint}`}>{fmt(row.value)}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">{m.unit}</div>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

export function MarketplacePage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'prompt', price_jxp: 50, content: '', preview_url: '' });
  const { user, refresh } = useAuth();
  const load = () => api.get('/marketplace/products').then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post('/marketplace/products', form); toast.success('Listed'); setOpen(false); load(); }
    catch (e) { toast.error('Failed'); }
  };
  const buy = async (id) => {
    try { const { data } = await api.post('/marketplace/purchase', { product_id: id }); toast.success('Purchased!'); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Store className="w-7 h-7 text-violet-400" /> Marketplace</h1>
          <p className="text-sm text-slate-400">Buy & sell prompts, art & templates with JXP</p>
        </div>
        {user && <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="mkt-list-btn" className="gradient-brand text-white">List item</Button></DialogTrigger>
          <DialogContent className="glass">
            <DialogHeader><DialogTitle>List item</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="mkt-title" placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
              <Textarea data-testid="mkt-desc" placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prompt">Prompt</SelectItem>
                  <SelectItem value="artwork">Artwork</SelectItem>
                  <SelectItem value="template">Template</SelectItem>
                  <SelectItem value="workflow">Workflow</SelectItem>
                  <SelectItem value="assistant">Assistant</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="mkt-price" type="number" placeholder="Price in JXP" value={form.price_jxp} onChange={e => setForm({...form, price_jxp: +e.target.value})} />
              <Input placeholder="Preview URL (optional)" value={form.preview_url} onChange={e => setForm({...form, preview_url: e.target.value})} />
              <Textarea placeholder="Content (buyer receives)" value={form.content} onChange={e => setForm({...form, content: e.target.value})} />
            </div>
            <DialogFooter><Button data-testid="mkt-create" onClick={create} className="gradient-brand text-white">Publish</Button></DialogFooter>
          </DialogContent>
        </Dialog>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {items.length === 0 && <Card className="glass p-10 col-span-full text-center text-slate-400">No products yet — be the first to list!</Card>}
        {items.map(p => (
          <Card key={p.id} data-testid={`prod-${p.id}`} className="glass p-4">
            {p.preview_url && <img src={p.preview_url} alt="" className="w-full aspect-video object-cover rounded mb-2" />}
            <div className="text-xs uppercase tracking-wider text-violet-400 font-semibold">{p.category}</div>
            <div className="font-display font-bold text-lg mt-1">{p.title}</div>
            <div className="text-xs text-slate-400 line-clamp-2 mt-1">{p.description}</div>
            <div className="flex justify-between items-center mt-3">
              <div className="font-bold text-amber-400">{p.price_jxp} JXP</div>
              <Button data-testid={`buy-${p.id}`} size="sm" onClick={() => buy(p.id)} className="gradient-brand text-white">Buy</Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function BoostsPage() {
  const { user, refresh, publicSettings } = useAuth();
  const buy = async (duration) => {
    try { await api.post('/boosts/purchase', { duration }); toast.success(`Boost ${duration} active`); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const prices = publicSettings?.boost_prices || {};
  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Rocket className="w-7 h-7 text-fuchsia-400" /> Profile Boosts</h1>
      <p className="text-sm text-slate-400">Boost your profile visibility temporarily</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[['1h', '1 Hour'], ['6h', '6 Hours'], ['24h', '24 Hours']].map(([k, label]) => (
          <Card key={k} className="glass p-5 text-center">
            <div className="font-display text-2xl font-bold">{label}</div>
            <div className="text-3xl font-black text-amber-400 my-3">{prices[k]} JXP</div>
            <Button data-testid={`boost-${k}`} onClick={() => buy(k)} className="w-full gradient-brand text-white">Activate</Button>
          </Card>
        ))}
      </div>
      {user?.boost_active_until && new Date(user.boost_active_until) > new Date() && (
        <Card className="glass p-4">
          <div className="text-sm">Active boost until: <span className="font-bold">{new Date(user.boost_active_until).toLocaleString()}</span></div>
        </Card>
      )}
    </div>
  );
}
